import React from 'react';
import { Helmet } from 'react-helmet-async';

const faqs = [
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'What are FoodLens lenses?',
        a: 'Each lens is a specialized detection tool that scans ingredients for a specific concern. FoodLens includes 16+ lenses covering allergens (nuts, soy, egg, dairy), medical conditions (celiac, PKU, G6PD, histamine), and health concerns (seed oils, preservatives, artificial colors, and more). All allergen and medical lenses are completely free.',
      },
      {
        q: 'Is FoodLens really free to use?',
        a: 'Yes. All allergen detection, medical condition lenses, and health lenses are free with no ads — forever. We believe food safety should never be paywalled. Premium ($0.99/mo) adds custom lenses, artificial sweetener detection, and a points multiplier.',
      },
      {
        q: 'Do I need to create an account?',
        a: 'No account or login is required to use FoodLens. You can start scanning and analyzing food products immediately after downloading the app or installing the extension.',
      },
    ],
  },
  {
    category: 'Medical & Allergen Lenses',
    questions: [
      {
        q: 'Which medical conditions does FoodLens support?',
        a: 'FoodLens has dedicated lenses for celiac disease (gluten detection), PKU/phenylketonuria (aspartame and phenylalanine), G6PD deficiency (fava beans, sulfites, quinine), and histamine intolerance. All medical lenses are free.',
      },
      {
        q: 'Which allergens can FoodLens detect?',
        a: 'FoodLens detects tree nuts and peanuts, soy (including derivatives like soy lecithin and TVP), egg (including albumin, lysozyme, and other hidden forms), and dairy (including casein, whey, and lactose). All allergen lenses are free.',
      },
      {
        q: 'How accurate is allergen detection?',
        a: 'FoodLens scans ingredient lists for known allergen terms and their common aliases. It is designed as an awareness tool to help you identify potential allergens, not as a medical device. Always verify with the manufacturer if you have a severe allergy, and look for certified allergen-free labels when needed.',
      },
      {
        q: 'Do you support FODMAP detection?',
        a: 'Not yet, but FODMAP detection is on our roadmap. FODMAPs require a nuanced approach since tolerance varies significantly between individuals, and we want to get it right. Stay tuned.',
      },
    ],
  },
  {
    category: 'Features',
    questions: [
      {
        q: 'What if a product isn\'t in your database?',
        a: 'If we don\'t have information about a product, you can submit the details manually. This helps us improve our database for all users, and earns you points. Your contributions help make FoodLens more accurate for everyone.',
      },
      {
        q: 'How does the browser extension work?',
        a: 'The FoodLens browser extension automatically scans product pages while you shop online on Amazon, Walmart, Whole Foods, and Instacart. It highlights flagged ingredients directly on the page — no extra steps needed. It is the only ingredient scanner designed for online grocery shopping.',
      },
      {
        q: 'What\'s the difference between the app and the extension?',
        a: 'The iOS app scans physical ingredient labels using your camera — for shopping in-store. The Chrome extension scans product pages automatically while you shop online. Together, they cover everywhere you buy food.',
      },
    ],
  },
  {
    category: 'Privacy & Data',
    questions: [
      {
        q: 'Is my scan history saved?',
        a: 'Your scan history is saved locally on your device for your convenience. We do not share this information with anybody, and we can\'t view it either. Your privacy is our priority.',
      },
      {
        q: 'Does FoodLens collect my data?',
        a: 'We collect minimal anonymous usage data to improve the app. We never sell your data, track what you eat, or share personal information with advertisers. See our Privacy Policy for full details.',
      },
    ],
  },
  {
    category: 'Points & Rewards',
    questions: [
      {
        q: 'How does the points to cash conversion work?',
        a: 'Every quarter, we distribute 10% of our profits to users based on their points. Your reward is proportional to your points vs. the total points earned by all users that quarter. Premium users earn 1.5x points on every action.',
      },
      {
        q: 'How do I redeem my points?',
        a: 'At the end of each quarter, eligible rewards are distributed via Venmo or digital gift cards. You\'ll receive a notification when your reward is ready to claim.',
      },
    ],
  },
];

function FAQ() {
  return (
    <>
      <Helmet>
        <title>FAQ - Frequently Asked Questions | FoodLens AI</title>
        <meta name="description" content="Find answers to common questions about FoodLens AI, including how to use the app, privacy information, and our rewards system." />
      </Helmet>

      <div className="bg-background min-h-screen">
        {/* Hero */}
        <section className="bg-gradient-hero py-16 md:py-24">
          <div className="section text-center">
            <h1 className="section-heading">Frequently Asked Questions</h1>
            <p className="section-subheading mx-auto">
              Everything you need to know about FoodLens.
            </p>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="section py-16">
          <div className="max-w-3xl mx-auto">
            {faqs.map((section) => (
              <div key={section.category} className="mb-12">
                <h2 className="font-display text-2xl text-sage-600 mb-6">{section.category}</h2>
                <div className="space-y-4">
                  {section.questions.map((faq) => (
                    <details
                      key={faq.q}
                      className="group bg-surface rounded-2xl border border-sage-100 overflow-hidden"
                    >
                      <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                        <h3 className="font-semibold text-charcoal pr-4">{faq.q}</h3>
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-sage-100 text-sage-600 flex items-center justify-center group-open:rotate-45 transition-transform">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                        </span>
                      </summary>
                      <div className="px-6 pb-6">
                        <p className="text-gray-500 leading-relaxed">{faq.a}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="max-w-3xl mx-auto mt-12 text-center">
            <div className="bg-sage-100 rounded-3xl p-8">
              <h3 className="font-display text-2xl text-charcoal mb-2">Still have questions?</h3>
              <p className="text-gray-500 mb-6">We're here to help.</p>
              <a
                href="mailto:foodlens@rainierresearch.xyz"
                className="btn-primary"
              >
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default FAQ;
